import React, { useEffect, useState } from 'react';

import {
  ChevronDownIcon,
  ChevronUpIcon,
  CircleAlertIcon,
  CircleCheckBig,
  Loader2,
  MailPlusIcon,
  SearchIcon,
  ShieldCheckIcon,
  X,
} from 'lucide-react';

import useFetchCarrierVerificationRequest from 'hooks/useFetchCarrierVerification';
import useFetchCarrierVerificationByDotMc from 'hooks/useFetchCarrierVerificationByDotMc';
import { useToast } from 'hooks/useToaster';
import FreightValidateLogo from 'icons/FreightValidateLogo';
import HighwayLogo from 'icons/HighwayLogo';
import MCPLogo from 'icons/MCPLogo';
import TruckstopLogo from 'icons/TruckstopLogo';
import { inviteCarrier } from 'lib/api/inviteCarrier';
import { CarrierContact, CarrierVerification } from 'types/CarrierVerification';
import { IntegrationCore } from 'types/Integration';
import { Maybe } from 'types/UtilityTypes';
import { ButtonNamePosthog } from 'types/enums/CustomPosthogEvents';
import { CarrierVerification as CarrierVerificationEnum } from 'types/enums/Integrations';
import {
  formatContactName,
  getAvailableContacts,
  getCarrierColors,
  getEmailSearchResultMessage,
  getInfractionSeverityColor,
  getWarningBannerClassName,
  getWarningTextClassName,
  getWarningType,
  getDisplayCarrier as resolveDisplayCarrier,
  sortInfractionsBySeverity,
} from 'utils/carrierVerificationHelpers';
import { getCarrierVerificationProfileUrl } from 'utils/carrierVerificationProfileUrl';
import { cn } from 'utils/shadcn';

import { Badge } from '../Badge';
import { Button } from '../Button';
import { Card, CardContent, CardFooter, CardHeader } from '../Card';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../Collapsible';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '../Dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../Dropdown';
import { Skeleton } from '../Skeleton';
import { Input } from '../input';
import { Flex } from '../layout';
import { Typography } from '../typography';
import CarrierVerificationEmailSearchResultMessage from './CarrierVerificationEmailSearchResultMessage';
import CarrierVerificationInviteDropdown from './CarrierVerificationInviteDropdown';
import CarrierVerificationMultipleCarriersSelector from './CarrierVerificationMultipleCarriersSelector';

interface CarrierVerificationCardProps {
  fromEmail: string;
  enabledIntegration: IntegrationCore;
  defaultExpanded?: boolean;
  /** When true (e.g. carrier verification–only sidebar), body stays open and chevron/title do not toggle collapse. */
  hideCollapseToggle?: boolean;
}

function carrierReviewStatusLabel(carrier: CarrierVerification): string {
  switch (carrier.integration_name) {
    case CarrierVerificationEnum.Highway:
    case CarrierVerificationEnum.FreightValidate:
    case CarrierVerificationEnum.Truckstop:
      return carrier.risk_rating;

    case CarrierVerificationEnum.MyCarrierPortal: {
      const mcpReviewRating = carrier.risk_rating.toLowerCase();
      switch (true) {
        case mcpReviewRating.includes('unacceptablefail'):
          return 'Unacceptable Fail';
        case mcpReviewRating.includes('unacceptablereview'):
          return 'Review Required';
        default:
          return '';
      }
    }
  }
  return '';
}

function CarrierStatusBadges({
  carrier,
  className,
}: {
  carrier: CarrierVerification;
  className?: string;
}) {
  const reviewStatus = carrierReviewStatusLabel(carrier);

  return (
    <Flex
      direction='row'
      align='center'
      gap='sm'
      className={cn('flex-wrap', className)}
    >
      {carrier.is_authorized_user ? (
        <Badge
          variant='outline'
          className='border-success-500 text-success-700 bg-success-50 text-xs px-2 py-0.5'
        >
          ✓ Authorized User
        </Badge>
      ) : (
        <Badge
          variant='outline'
          className='border-warning-500 text-warning-700 bg-warning-50 text-xs px-2 py-0.5'
        >
          ⏳ Pending User Auth
        </Badge>
      )}
      {reviewStatus ? (
        <Badge
          variant='outline'
          className={`text-xs px-2 py-0.5 ${getCarrierColors(carrier).badge}`}
        >
          {reviewStatus}
        </Badge>
      ) : null}
    </Flex>
  );
}

export default function CarrierVerificationCard({
  fromEmail,
  enabledIntegration,
  defaultExpanded = false,
  hideCollapseToggle = false,
}: CarrierVerificationCardProps) {
  const { carrier, isLoading } = useFetchCarrierVerificationRequest(fromEmail);
  const { toast } = useToast();
  const [inviteLoading, setInviteLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(defaultExpanded);
  const [selectedContact, setSelectedContact] =
    useState<Maybe<CarrierContact>>(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [selectedCarrierIndex, setSelectedCarrierIndex] = useState(0);
  const [isInfractionsOpen, setIsInfractionsOpen] = useState(false);
  const [isSearchMode, setIsSearchMode] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [searchType, setSearchType] = useState<'dot' | 'mc'>('dot');
  const [searchDotNumber, setSearchDotNumber] = useState<Maybe<string>>(null);
  const [searchMcNumber, setSearchMcNumber] = useState<Maybe<string>>(null);

  // Search by DOT/MC number - only fetch when we have a valid search value
  const hasSearchValue = !!(searchDotNumber || searchMcNumber);
  const { carrier: searchCarrier, isLoading: isSearchLoading } =
    useFetchCarrierVerificationByDotMc(
      searchDotNumber,
      searchMcNumber,
      hasSearchValue
    );

  // Use search result if search is active, otherwise use email-based result
  const activeCarrier = hasSearchValue ? searchCarrier : carrier;
  const activeIsLoading = hasSearchValue ? isSearchLoading : isLoading;

  // Reset selected sub-carrier index when the active carrier changes (e.g. new DOT/MC search).
  // Otherwise a stale index (e.g. 3) would pick the wrong sub-carrier from the new result's all_carriers.
  const carrierIdentity = hasSearchValue
    ? `search-${searchDotNumber ?? ''}-${searchMcNumber ?? ''}`
    : `email-${carrier?.dot_number ?? 'none'}`;
  useEffect(() => {
    setSelectedCarrierIndex(0);
  }, [carrierIdentity]);

  // When search completes with a result, close search mode so the carrier title is visible
  useEffect(() => {
    if (hasSearchValue && searchCarrier && !isSearchLoading) {
      setIsSearchMode(false);
    }
  }, [hasSearchValue, searchCarrier, isSearchLoading]);

  function getDisplayCarrier(): Maybe<CarrierVerification> {
    return resolveDisplayCarrier(activeCarrier, selectedCarrierIndex);
  }

  const handleSearchSubmit = () => {
    const trimmed = searchInput.trim();
    if (!trimmed) {
      setSearchDotNumber(null);
      setSearchMcNumber(null);
      setIsSearchMode(false);
      return;
    }
    const digitsOnly = trimmed.replace(/\D/g, '');
    if (!digitsOnly) {
      setSearchDotNumber(null);
      setSearchMcNumber(null);
      return;
    }
    if (searchType === 'dot') {
      setSearchDotNumber(digitsOnly);
      setSearchMcNumber(null);
    } else {
      setSearchMcNumber(digitsOnly);
      setSearchDotNumber(null);
    }
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSearchSubmit();
    }
    if (e.key === 'Escape') {
      setIsSearchMode(false);
      setSearchInput('');
      // Match handleCancelSearch: only clear search state when there's no result yet
      if (!searchCarrier || isSearchLoading) {
        setSearchDotNumber(null);
        setSearchMcNumber(null);
      }
    }
  };

  const handleCancelSearch = () => {
    setIsSearchMode(false);
    setSearchInput('');
    // Only clear search state (revert to email-based carrier) when there's no result yet.
    // If we already have a search result, keep it so cancel just closes the search UI.
    if (!searchCarrier || isSearchLoading) {
      setSearchDotNumber(null);
      setSearchMcNumber(null);
    }
  };

  function cardActions(displayCarrier: Maybe<CarrierVerification>) {
    if (!displayCarrier) return { viewProfile: null, invite: null };

    const isUnauthorized = (() => {
      const rating = displayCarrier.risk_rating.toLowerCase();
      switch (displayCarrier.integration_name) {
        case CarrierVerificationEnum.Highway:
          return rating.includes('not authorized');
        case CarrierVerificationEnum.MyCarrierPortal:
          return rating.includes('unacceptablefail');
        case CarrierVerificationEnum.FreightValidate:
          return rating.includes('warning');
        case CarrierVerificationEnum.Truckstop:
          return rating.includes('unauthorized');
        default:
          return false;
      }
    })();

    const profileUrl = getCarrierVerificationProfileUrl(displayCarrier);

    const viewProfile = profileUrl ? (
      <a
        key='View Profile'
        className='group flex items-center justify-center gap-2 h-11 px-4 min-w-0 flex-1 sm:flex-initial sm:w-40 rounded-[4px] text-sm font-medium border border-neutral-300 bg-neutral-50 text-neutral-900 hover:bg-neutral-100 hover:border-neutral-400 transition-colors'
        href={profileUrl}
        target='_blank'
        rel='noopener noreferrer'
      >
        {(() => {
          switch (displayCarrier.integration_name) {
            case CarrierVerificationEnum.MyCarrierPortal:
              return (
                <MCPLogo
                  fill='#4786FE'
                  className='group-hover:fill-neutral-500 transition-colors shrink-0'
                  width={20}
                  height={20}
                />
              );
            case CarrierVerificationEnum.Highway:
              return (
                <HighwayLogo
                  fill='#F8C617'
                  className='group-hover:fill-neutral-500 transition-colors shrink-0'
                  width={20}
                  height={20}
                />
              );
            case CarrierVerificationEnum.FreightValidate:
              return (
                <FreightValidateLogo
                  fill='#4786FE'
                  className='group-hover:fill-neutral-500 transition-colors shrink-0'
                  width={20}
                  height={20}
                />
              );
            case CarrierVerificationEnum.Truckstop:
              return (
                <TruckstopLogo
                  fill='#D90119'
                  className='group-hover:fill-neutral-500 transition-colors shrink-0'
                  width={20}
                  height={20}
                />
              );
            default:
              return null;
          }
        })()}
        <span>View Profile</span>
      </a>
    ) : null;

    // Use original carrier for contacts since displayCarrier might not have them when there are multiple carriers
    const availableContacts = getAvailableContacts(activeCarrier);
    const allEmails = activeCarrier?.all_emails || [];
    // Check if we have any emails to show (contacts or all_emails)
    const hasAnyEmails = availableContacts.length > 0 || allEmails.length > 0;

    // Determine button state based on packet_invite_status or completed_packet
    const inviteStatus = displayCarrier?.packet_invite_status;
    const isInNetwork = displayCarrier?.completed_packet;
    const shouldDisableInvite = inviteLoading || isInNetwork || !!inviteStatus;

    // Determine button label and icon
    const getInviteButtonContent = () => {
      if (inviteLoading) {
        return (
          <>
            <Loader2 className='h-4 w-4 animate-spin mr-2 shrink-0' />
            <span>Sending...</span>
          </>
        );
      }

      if (inviteStatus) {
        return (
          <>
            <CircleCheckBig className='h-4 w-4 mr-2 shrink-0' />
            <span>{inviteStatus}</span>
          </>
        );
      }

      if (isInNetwork) {
        return (
          <>
            <CircleCheckBig className='h-4 w-4 mr-2 shrink-0' />
            <span className='whitespace-normal wrap-break-word'>
              In Network
            </span>
          </>
        );
      }

      return (
        <>
          <MailPlusIcon className='h-4 w-4 mr-2 shrink-0' />
          <span>Invite</span>
        </>
      );
    };

    const inviteButton = (
      <Button
        key='Invite'
        onClick={
          hasAnyEmails || shouldDisableInvite
            ? undefined
            : () => {
                setSelectedContact(null);
                setShowConfirmDialog(true);
              }
        }
        disabled={shouldDisableInvite}
        variant='outline'
        className='min-w-0 max-w-full flex-1 sm:flex-initial sm:w-40 text-sm px-4 whitespace-normal overflow-x-hidden'
        buttonNamePosthog={ButtonNamePosthog.CarrierVerificationInvite}
      >
        {getInviteButtonContent()}
      </Button>
    );

    const handleEmailSelect = (email: string, contact?: CarrierContact) => {
      if (contact) {
        setSelectedContact(contact);
      } else {
        // Create a minimal contact object for email-only entries
        setSelectedContact({
          first_name: '',
          last_name: '',
          title: '',
          phone: '',
          email,
          authorized_for_packets: false,
          verification_status: null,
        });
      }
      setShowConfirmDialog(true);
    };

    let invite: React.ReactNode = null;
    if (!isUnauthorized) {
      if (
        displayCarrier?.integration_name ===
        CarrierVerificationEnum.MyCarrierPortal
      ) {
        if (hasAnyEmails) {
          invite = (
            <CarrierVerificationInviteDropdown
              contacts={availableContacts}
              allEmails={allEmails}
              inviteButton={inviteButton}
              onEmailSelect={handleEmailSelect}
              formatContactName={formatContactName}
            />
          );
        } else {
          invite = inviteButton;
        }
      } else if (
        displayCarrier?.integration_name !== CarrierVerificationEnum.Highway
      ) {
        invite = inviteButton;
      }
    }

    return { viewProfile, invite };
  }

  async function onInvite() {
    if (!displayCarrier) return;

    setInviteLoading(true);
    const emailToUse = selectedContact?.email || fromEmail;

    const res = await inviteCarrier({
      dot_number: displayCarrier.dot_number.toString(),
      email: emailToUse,
    });

    if (res.isOk()) {
      const contactName =
        selectedContact?.first_name || selectedContact?.last_name
          ? `${selectedContact.first_name} ${selectedContact.last_name}`.trim()
          : null;
      toast({
        description: `Carrier invited${emailToUse ? ` to ${contactName || emailToUse}` : ''}.`,
        variant: 'success',
      });
      setShowConfirmDialog(false);
      setSelectedContact(null);
    } else {
      toast({ description: res.error.message, variant: 'destructive' });
    }

    setInviteLoading(false);
  }

  const carrierNotFound = !activeCarrier && !activeIsLoading;
  const displayCarrier = getDisplayCarrier();
  const hasMultipleCarriers =
    activeCarrier &&
    ((activeCarrier.total_carriers_found &&
      activeCarrier.total_carriers_found > 1) ||
      (activeCarrier.all_carriers && activeCarrier.all_carriers.length > 1));
  const emailSearchResultMessage = !hasMultipleCarriers
    ? getEmailSearchResultMessage(activeCarrier?.email_search_result_category)
    : null;

  return (
    <div className='px-2 sm:px-3 w-full max-w-full'>
      <Card
        className={`w-full max-w-full min-w-0 mt-4 relative overflow-hidden rounded-[4px] bg-neutral-50/50 border-neutral-200 ${getCarrierColors(displayCarrier).border}`}
      >
        <Collapsible
          open={hideCollapseToggle ? true : isOpen}
          onOpenChange={hideCollapseToggle ? () => {} : setIsOpen}
        >
          {/* Header is not a single <button>: search field and icon buttons must not nest inside CollapsibleTrigger (invalid HTML + toggles open on input focus). */}
          <div className='flex w-full max-w-full min-w-0 items-center gap-2 px-2 py-2.5 sm:px-3'>
            <span className='shrink-0 flex h-8 w-8 items-center justify-center'>
              {(() => {
                const integrationToShow =
                  activeCarrier?.integration_name || enabledIntegration?.name;

                switch (integrationToShow) {
                  case CarrierVerificationEnum.MyCarrierPortal:
                    return <MCPLogo fill='#4786FE' width={20} height={20} />;
                  case CarrierVerificationEnum.Highway:
                    return (
                      <HighwayLogo fill='#F8C617' width={20} height={20} />
                    );
                  case CarrierVerificationEnum.FreightValidate:
                    return (
                      <FreightValidateLogo
                        fill='#4786FE'
                        width={20}
                        height={20}
                      />
                    );
                  case CarrierVerificationEnum.Truckstop:
                    return (
                      <TruckstopLogo fill='#D90119' width={20} height={20} />
                    );
                  default:
                    return (
                      <ShieldCheckIcon
                        className='text-neutral-500 shrink-0'
                        width={20}
                        height={20}
                      />
                    );
                }
              })()}
            </span>

            {isSearchMode ? (
              <Flex
                direction='col'
                gap='xs'
                className='flex-1 min-w-0 max-w-full justify-center overflow-hidden'
              >
                <Flex
                  direction='row'
                  align='center'
                  gap='xs'
                  className='min-h-8 min-w-0 max-w-full w-full overflow-x-auto overflow-y-hidden [scrollbar-width:thin]'
                >
                  <Flex
                    direction='row'
                    className='min-w-0 flex-1 basis-0'
                    align='stretch'
                  >
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button
                          type='button'
                          className='h-8 px-2 sm:px-3 flex items-center justify-center gap-0.5 shrink-0 border border-neutral-input-border border-r-0 rounded-l-[4px] bg-neutral-input-bg text-xs sm:text-[13px] leading-none text-neutral-input-text hover:bg-neutral-200 transition-colors'
                        >
                          <span className='font-medium whitespace-nowrap'>
                            {searchType === 'dot' ? 'DOT' : 'MC'}
                          </span>
                          <ChevronDownIcon className='h-3.5 w-3.5 sm:h-4 sm:w-4 shrink-0 text-neutral-500' />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align='start' sideOffset={2}>
                        <DropdownMenuItem onSelect={() => setSearchType('dot')}>
                          DOT
                        </DropdownMenuItem>
                        <DropdownMenuItem onSelect={() => setSearchType('mc')}>
                          MC
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                    <Input
                      type='text'
                      inputMode='numeric'
                      value={searchInput}
                      onChange={(e) => setSearchInput(e.target.value)}
                      onKeyDown={handleSearchKeyDown}
                      placeholder={
                        searchType === 'dot' ? 'DOT number' : 'MC number'
                      }
                      className='min-w-0 flex-1 basis-0 h-8 min-h-8 rounded-l-none border-l-0 bg-input-bg px-2 sm:px-2.5 py-0 text-xs sm:text-[13px] leading-none text-input-text placeholder:text-input-placeholder'
                      autoFocus
                    />
                  </Flex>
                  <Button
                    variant='ghost'
                    size='xs'
                    onClick={() => handleSearchSubmit()}
                    disabled={isSearchLoading}
                    className='h-8 w-8 min-h-8 min-w-8 shrink-0 p-0 box-border overflow-hidden'
                    buttonNamePosthog={null}
                  >
                    {isSearchLoading ? (
                      <Loader2 className='h-4 w-4 shrink-0 animate-spin' />
                    ) : (
                      <SearchIcon className='h-4 w-4 shrink-0' />
                    )}
                  </Button>
                  <Button
                    variant='ghost'
                    size='xs'
                    onClick={() => handleCancelSearch()}
                    className='h-8 w-8 min-h-8 min-w-8 shrink-0 p-0 box-border overflow-hidden'
                    buttonNamePosthog={null}
                  >
                    <X className='h-4 w-4 shrink-0' />
                  </Button>
                </Flex>
                {displayCarrier && (
                  <CarrierStatusBadges carrier={displayCarrier} />
                )}
              </Flex>
            ) : (
              <div className='flex flex-1 min-w-0 max-w-full items-center gap-1'>
                {(() => {
                  const titleHeaderButton = (
                    <Button
                      type='button'
                      variant='ghost'
                      className={cn(
                        'flex-1 min-w-0 max-w-full h-auto flex flex-col items-stretch justify-center gap-0 text-left px-1 py-0 rounded-[4px] border border-transparent hover:border-transparent',
                        hideCollapseToggle
                          ? 'cursor-default hover:bg-transparent focus-visible:ring-0'
                          : 'hover:bg-neutral-100/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-main focus-visible:ring-offset-1'
                      )}
                      buttonNamePosthog={
                        ButtonNamePosthog.CarrierVerificationInvite
                      }
                    >
                      <Flex
                        direction='row'
                        align='center'
                        gap='xs'
                        className='min-h-8 w-full min-w-0'
                      >
                        {displayCarrier && (
                          <Typography
                            variant='body-sm'
                            weight='medium'
                            className='truncate flex-1 min-w-0'
                            title={displayCarrier.name}
                          >
                            {displayCarrier.name}
                          </Typography>
                        )}
                        {carrierNotFound && !hasSearchValue && (
                          <Typography
                            variant='body-sm'
                            textColor='muted'
                            className='flex-1 min-w-0'
                          >
                            No carrier found
                          </Typography>
                        )}
                        {activeIsLoading && !displayCarrier && (
                          <Typography
                            variant='body-sm'
                            textColor='muted'
                            className='flex-1 min-w-0'
                          >
                            Loading...
                          </Typography>
                        )}
                        {!displayCarrier &&
                          !carrierNotFound &&
                          !activeIsLoading &&
                          !hasSearchValue && (
                            <Typography
                              variant='body-sm'
                              textColor='muted'
                              className='flex-1 min-w-0'
                            >
                              Search for a carrier
                            </Typography>
                          )}
                      </Flex>
                      {displayCarrier && (
                        <CarrierStatusBadges
                          carrier={displayCarrier}
                          className='mt-2'
                        />
                      )}
                    </Button>
                  );
                  return hideCollapseToggle ? (
                    titleHeaderButton
                  ) : (
                    <CollapsibleTrigger asChild>
                      {titleHeaderButton}
                    </CollapsibleTrigger>
                  );
                })()}
                <Button
                  variant='ghost'
                  size='xs'
                  onClick={() => setIsSearchMode(true)}
                  className='h-8 w-8 min-h-8 min-w-8 shrink-0 p-0 box-border overflow-hidden'
                  buttonNamePosthog={null}
                >
                  <SearchIcon className='h-4 w-4 shrink-0' />
                </Button>
              </div>
            )}

            {!hideCollapseToggle && (
              <CollapsibleTrigger asChild>
                <Button
                  variant='ghost'
                  className='h-8 w-8 min-h-8 min-w-8 shrink-0 p-0 box-border overflow-hidden'
                  buttonNamePosthog={null}
                >
                  {isOpen ? (
                    <ChevronUpIcon className='h-4 w-4 shrink-0' />
                  ) : (
                    <ChevronDownIcon className='h-4 w-4 shrink-0' />
                  )}
                </Button>
              </CollapsibleTrigger>
            )}
          </div>

          <CollapsibleContent className='min-w-0 max-w-full overflow-x-hidden'>
            {carrierNotFound && (
              <CardContent className='px-3 sm:px-4 pt-0 pb-6 min-w-0 max-w-full overflow-x-hidden'>
                <Typography variant='body-sm' textColor='muted' align='center'>
                  No carrier information available for this email address.
                </Typography>
              </CardContent>
            )}
            {!carrierNotFound && activeIsLoading && (
              <CardContent className='space-y-2 px-3 sm:px-4 pt-0 pb-6 min-h-[200px] max-w-full overflow-hidden'>
                <Skeleton className='h-4 w-full max-w-full' />
                <Skeleton className='h-4 w-full max-w-full' />
              </CardContent>
            )}
            {!carrierNotFound && !activeIsLoading && (
              <>
                <CardHeader className='p-0 px-3 sm:px-4 pb-2'></CardHeader>
                <CardContent className='space-y-2 px-3 sm:px-4 pt-2 pb-4 min-w-0 max-w-full overflow-x-hidden'>
                  {/* Warnings Banner */}
                  {activeCarrier?.warnings &&
                    activeCarrier.warnings.length > 0 && (
                      <Flex direction='col' gap='xs' className='mb-2'>
                        {(activeCarrier.warnings || [])
                          .filter((warning) => {
                            const warningLower = warning.toLowerCase();
                            return !warningLower.includes('multiple carriers');
                          })
                          .map((warning, index) => {
                            const warningType = getWarningType(warning);

                            return (
                              <Flex
                                key={index}
                                align='center'
                                gap='sm'
                                className={`rounded-lg border p-3 w-full ${getWarningBannerClassName(warningType)}`}
                              >
                                <CircleAlertIcon
                                  className={`h-4 w-4 shrink-0 ${getWarningTextClassName(warningType)}`}
                                />
                                <Typography
                                  variant='body-sm'
                                  className={getWarningTextClassName(
                                    warningType
                                  )}
                                >
                                  {warning}
                                </Typography>
                              </Flex>
                            );
                          })}
                      </Flex>
                    )}

                  {/* Multiple Carriers Selector */}
                  {hasMultipleCarriers && activeCarrier.all_carriers && (
                    <CarrierVerificationMultipleCarriersSelector
                      allCarriers={activeCarrier.all_carriers}
                      totalCarriersFound={activeCarrier.total_carriers_found}
                      selectedCarrierName={displayCarrier?.name ?? null}
                      onCarrierSelect={setSelectedCarrierIndex}
                    />
                  )}

                  {/* Email Search Result Category Message (only if not multiple carriers) */}
                  {emailSearchResultMessage && (
                    <CarrierVerificationEmailSearchResultMessage
                      message={emailSearchResultMessage.message}
                      type={emailSearchResultMessage.type}
                    />
                  )}
                  {displayCarrier?.dba && (
                    <div>
                      <Typography
                        variant='body-xs'
                        textColor='muted'
                        className='uppercase tracking-wide'
                      >
                        DBA
                      </Typography>
                      <Typography variant='body-sm' className='mt-1'>
                        {displayCarrier.dba}
                      </Typography>
                    </div>
                  )}
                  <Flex direction='row' gap='lg' wrap='wrap'>
                    <div className='flex-1 min-w-0'>
                      <Typography
                        variant='body-xs'
                        textColor='muted'
                        className='uppercase tracking-wide'
                      >
                        DOT #
                      </Typography>
                      <Typography variant='body-sm' className='mt-1'>
                        {displayCarrier?.dot_number}
                      </Typography>
                    </div>
                    <div className='flex-1 min-w-0'>
                      <Typography
                        variant='body-xs'
                        textColor='muted'
                        className='uppercase tracking-wide'
                      >
                        MC #
                      </Typography>
                      <Typography variant='body-sm' className='mt-1'>
                        {displayCarrier?.docket_number}
                      </Typography>
                    </div>
                  </Flex>
                  {/* Location Display */}
                  {(displayCarrier?.city ||
                    displayCarrier?.state ||
                    displayCarrier?.zip_code) && (
                    <div>
                      <Typography
                        variant='body-xs'
                        textColor='muted'
                        className='uppercase tracking-wide'
                      >
                        Location
                      </Typography>
                      <Typography variant='body-sm' className='mt-1'>
                        {(() => {
                          const parts = [
                            displayCarrier.city,
                            displayCarrier.state,
                            displayCarrier.zip_code,
                          ].filter(Boolean);
                          // Format as "City, State ZIP"
                          if (parts.length === 3) {
                            return `${parts[0]}, ${parts[1]} ${parts[2]}`;
                          }
                          return parts.join(', ');
                        })()}
                      </Typography>
                    </div>
                  )}
                  {/* Infractions Section */}
                  {displayCarrier?.infractions &&
                    displayCarrier.infractions.length > 0 && (
                      <div className='w-full mt-2'>
                        <Collapsible
                          open={isInfractionsOpen}
                          onOpenChange={setIsInfractionsOpen}
                        >
                          <CollapsibleTrigger asChild>
                            <Button
                              variant='ghost'
                              className='w-full justify-between p-2 h-auto hover:bg-neutral-100/50 focus:outline-none hover:outline-none focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-offset-0 hover:border-transparent focus:border-transparent'
                              buttonNamePosthog={null}
                            >
                              <Flex
                                direction='row'
                                align='center'
                                gap='sm'
                                className='text-left flex-1 min-w-0'
                              >
                                <Typography
                                  variant='body-xs'
                                  textColor='muted'
                                  className='uppercase tracking-wide'
                                >
                                  Infractions
                                </Typography>
                                <Badge
                                  variant='outline'
                                  className='border-neutral-500 text-neutral-700 bg-neutral-50 text-xs px-2 py-0.5'
                                >
                                  {displayCarrier.infractions.length}
                                </Badge>
                              </Flex>
                              {isInfractionsOpen ? (
                                <ChevronUpIcon className='h-4 w-4 shrink-0' />
                              ) : (
                                <ChevronDownIcon className='h-4 w-4 shrink-0' />
                              )}
                            </Button>
                          </CollapsibleTrigger>
                          <CollapsibleContent>
                            <Flex
                              direction='col'
                              gap='sm'
                              className='mt-2 w-full'
                            >
                              {sortInfractionsBySeverity(
                                displayCarrier.infractions
                              ).map((infraction, index) => (
                                <Flex
                                  key={index}
                                  direction='col'
                                  gap='xs'
                                  className='w-full rounded-lg border border-neutral-200 bg-neutral-50 p-3'
                                >
                                  <Flex
                                    direction='row'
                                    align='center'
                                    gap='sm'
                                    wrap='wrap'
                                  >
                                    <Badge
                                      variant='outline'
                                      className='border-neutral-500 text-neutral-700 bg-neutral-50 text-xs px-2 py-0.5'
                                    >
                                      {infraction.category}
                                    </Badge>
                                    <Badge
                                      variant='outline'
                                      className={`text-xs px-2 py-0.5 ${getInfractionSeverityColor(infraction.severity)}`}
                                    >
                                      {infraction.severity}
                                    </Badge>
                                  </Flex>
                                  <Typography
                                    variant='body-sm'
                                    className='text-neutral-700'
                                  >
                                    {infraction.rule_text}
                                  </Typography>
                                </Flex>
                              ))}
                            </Flex>
                          </CollapsibleContent>
                        </Collapsible>
                      </div>
                    )}
                </CardContent>
                {!carrierNotFound && !activeIsLoading && (
                  <CardFooter className='px-3 sm:px-4 py-0 pb-4 pt-0 mt-0 min-w-0 max-w-full overflow-x-hidden'>
                    <Flex
                      direction='row'
                      justify='center'
                      wrap='wrap'
                      gap='sm'
                      className='w-full min-w-0 max-w-full'
                    >
                      {Object.values(cardActions(displayCarrier)).filter(
                        Boolean
                      )}
                    </Flex>
                  </CardFooter>
                )}
              </>
            )}
          </CollapsibleContent>
        </Collapsible>
      </Card>

      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent>
          <Flex direction='col' gap='sm' className='p-4'>
            <DialogTitle>Confirm Carrier Invitation</DialogTitle>
            <DialogDescription>
              <Typography variant='body-sm'>
                {selectedContact?.email ? (
                  <>
                    Send carrier invitation to{' '}
                    <Typography variant='body-sm' weight='bold' as='span'>
                      {selectedContact.first_name || selectedContact.last_name
                        ? formatContactName(selectedContact)
                        : selectedContact.email}
                      {selectedContact.title && ` - ${selectedContact.title}`}
                    </Typography>
                    ?
                  </>
                ) : (
                  <>
                    Send carrier invitation to{' '}
                    <Typography variant='body-sm' weight='bold' as='span'>
                      {fromEmail}
                    </Typography>
                    ?
                  </>
                )}
              </Typography>
            </DialogDescription>
            <Flex
              direction='row'
              gap='sm'
              justify='end'
              className='w-full mt-4'
            >
              <Button
                variant='outline'
                size='sm'
                onClick={() => {
                  setShowConfirmDialog(false);
                  setSelectedContact(null);
                }}
                disabled={inviteLoading}
                buttonNamePosthog={null}
              >
                Cancel
              </Button>
              <Button
                size='sm'
                onClick={onInvite}
                disabled={inviteLoading}
                buttonNamePosthog={ButtonNamePosthog.CarrierVerificationInvite}
              >
                {inviteLoading ? (
                  <>
                    <Loader2 className='h-4 w-4 animate-spin mr-2' />
                    Sending...
                  </>
                ) : (
                  'Send Invitation'
                )}
              </Button>
            </Flex>
          </Flex>
        </DialogContent>
      </Dialog>
    </div>
  );
}
